import React, { useState } from 'react';
import { Dialog, Typography, Button, Box, TextField, CircularProgress } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import BankingService from 'services/banking.service';
import { EPixOperationType } from 'types/banking';
import { formatCurrency, parseCurrency } from 'utils/banking';
import SuccessModal from './SuccessModal';

type PamcardFormValues = {
  amount: string;
  description: string;
};

type PamcardLoadModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const validationSchema = Yup.object({
  amount: Yup.string()
    .required('Valor obrigatório')
    .test('non-zero', 'Informe um valor válido', (value) => value !== 'R$ 0,00'),
  description: Yup.string().nullable(),
});

const PamcardModal = ({ open, onClose, onSuccess }: PamcardLoadModalProps) => {
  const [successOpen, setSuccessOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const bankingService = new BankingService();
  const { enqueueSnackbar } = useSnackbar();

  const initialValues: PamcardFormValues = {
    amount: '',
    description: '',
  };

  const handleSubmit = async (values: PamcardFormValues) => {
    try {
      setLoading(true);

      await bankingService.createTransaction(EPixOperationType.PAMCARD, {
        value: parseCurrency(values.amount),
        description: values.description || undefined,
      });

      enqueueSnackbar('Carga PAMCARD criada com sucesso.', { variant: 'success' });
      setSuccessOpen(true);
      onSuccess();
    } catch (error) {
      enqueueSnackbar('Erro ao criar carga PAMCARD. Tente novamente.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <Box p={4}>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} validateOnMount>
            {({ values, errors, touched, handleChange, setFieldValue, isValid }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap={5}>
                  <Typography variant="h5" fontWeight={600} textAlign="center">
                    Carregar meu Pamcard
                  </Typography>

                  <TextField
                    fullWidth
                    variant="standard"
                    label="Valor (R$) *"
                    placeholder="R$ 0,00"
                    value={values.amount}
                    onChange={(e) => setFieldValue('amount', formatCurrency(e.target.value))}
                    error={touched.amount && Boolean(errors.amount)}
                    helperText={touched.amount && errors.amount}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        fontSize: '16px',
                        fontWeight: 500,
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    variant="standard"
                    label="Descrição (opcional)"
                    placeholder="Pagamento de fornecedor"
                    value={values.description}
                    onChange={handleChange('description')}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        fontSize: '16px',
                        fontWeight: 500,
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <Box display="flex" gap={2} mt={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={onClose}
                      disabled={loading}
                      sx={{
                        height: 56,
                        borderRadius: 999,
                        fontWeight: 600,
                        fontSize: 16,
                      }}
                    >
                      Cancelar
                    </Button>

                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={loading || !isValid}
                      sx={{
                        height: 56,
                        borderRadius: 999,
                        fontWeight: 600,
                        fontSize: 16,
                      }}
                    >
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Confirmar'}
                    </Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Dialog>

      <SuccessModal open={successOpen} onClose={handleSuccessClose} />
    </>
  );
};

export default PamcardModal;
